import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, message, Tooltip, Tag } from 'antd';
import { EditOutlined, SearchOutlined, ReloadOutlined, LaptopOutlined, DesktopOutlined } from '@ant-design/icons';
import { getUsersWithPermissions } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import './Permissions.scss';
import { getPermissionLabel } from '../utils/PermissionContentMap';

const getPermissionsTooltipContent = (permission) => {
  return (
    <div>
      <p>Permission ID: {permission.id}</p>
      <p>Active: {permission.active ? '✅' : '❌'}</p>
      <p>Type: {getPermissionLabel(permission.type)}</p>
      <p>Device Bindings: {permission.current_bindings} / {permission.max_bindings}</p>
      <p>IP: {permission.ip || 'N/A'}</p>
    </div>
  );
};

const Permissions = ({ setTitle }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const navigate = useNavigate();

  // console.log('Permissions component rendered'); // New console log

  const fetchUsers = useCallback(async () => {
    console.log('fetchUsers called'); // New console log
    setLoading(true);
    try {
      const data = await getUsersWithPermissions();

      if (Array.isArray(data)) {
        setUsers(data);
        // message.success('User permissions loaded successfully');
      } else {
        // message.error('Received invalid data format');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error details:', error.message);
      }
      message.error('Failed to fetch users: ' + (error.response?.data?.message || error.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setTitle('EarthVerse CRM - 權限管理');
    fetchUsers();
  }, [setTitle, fetchUsers]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleSort = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };


  const handleEdit = (user) => {
    navigate(`/permissions/${user.id}/edit`);
  };

  const handleViewDevices = (user) => {
    navigate(`/devices/${user.id}`);
  };

  const handleColor = (active) => {
    return active ? 'blue' : 'red';
  }
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      sortOrder: sortedInfo.columnKey === 'id' && sortedInfo.order,
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
    },
    {
      title: '是否具有可用權限',
      dataIndex: 'permissions_count',
      key: 'hasPermissions',
      render: (permissions_count) => (permissions_count > 0 ? '✅' : ''),
      sorter: (a, b) => a.permissions_count - b.permissions_count,
      sortOrder: sortedInfo.columnKey === 'hasPermissions' && sortedInfo.order,
    },
    {
      title: '現正綁定 / 授權總數量',
      dataIndex: 'permissions_content',
      key: 'permissions',
      render: (permissions_content, record) => {
        let totalBindings = 0;
        if (permissions_content && permissions_content.length > 0) {
          for (let i = 0; i < permissions_content.length; i++) {
            totalBindings += permissions_content[i].current_bindings || 0;
          }
        }
        return `${totalBindings} / ${record.permissions_count}`;
      },
      sortOrder: sortedInfo.columnKey === 'permissionCount' && sortedInfo.order,
    },
    {
      title: '快速查看權限',
      dataIndex: 'permissions_content',
      key: 'permissions_content',
      render: (permissions_content) => (
        <>
          {Array.isArray(permissions_content) && permissions_content.map((permission) => (
            <Tooltip key={permission.id} title={getPermissionsTooltipContent(permission)} placement="top">
              <Tag color={handleColor(permission.active)}>
                {getPermissionLabel(permission.type)}
              </Tag>
            </Tooltip>
          ))}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          />
          <Button
            icon={<DesktopOutlined />}
            onClick={() => handleViewDevices(record)}
            style={{ marginRight: 8 }}
          />
        </>
      ),
    },
  ];

  const filteredUsers = users.filter(
    (user) => user.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="permissions-container">
      <h2>用戶授權管理</h2>
      <div className="permissions-header">
        <Input
          placeholder="Search users"
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 200, marginRight: 16 }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchUsers}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        loading={loading}
        onChange={handleSort}
        pagination={{ pageSize: 30 }}
      />
    </div>
  );
};

export default Permissions;
